/**
 * useCurrentContext — lit `route.handle.context` de la route courante.
 *
 * @remarks
 * **Fail-loud** : si aucun `handle.context` n'est trouvé sur la route active,
 * on throw une erreur dev-friendly. C'est l'inverse du legacy qui retournait
 * `'dashboard'` (un contexte qui n'existait plus dans `CONTEXTS`) → bugs
 * silencieux pendant des heures.
 *
 * @example
 * ```tsx
 * // Dans une route :
 * { path: '/skill-up/session/:id', handle: { context: CONTEXTS.SKILL_UP_SESSION } }
 *
 * // Dans un composant :
 * const ctx = useCurrentContext();  // 'skill-up-session'
 * ```
 */

import { useMatches } from 'react-router-dom';
import type { Context } from '@/app/registry';

interface RouteHandleWithContext {
  context: Context;
}

function isHandleWithContext(handle: unknown): handle is RouteHandleWithContext {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    'context' in handle &&
    typeof handle.context === 'string'
  );
}

export function useCurrentContext(): Context {
  const matches = useMatches();

  // On parcourt les matches du plus profond au plus large : la route active
  // (la plus profonde) gagne si elle déclare un context.
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i]?.handle;
    if (isHandleWithContext(handle)) {
      return handle.context;
    }
  }

  throw new Error(
    `[useCurrentContext] Aucune route active n'a de handle.context. ` +
      `Vérifie ta config de route — toute route rendue dans le AppShell doit ` +
      `déclarer { handle: { context: CONTEXTS.XXX } }.`,
  );
}
